import { NOTO_EMOJI_FONT_CHUNKS, notoEmojiFontUrl } from './notoEmojiFont';

/**
 * A font family that draws emoji as Noto artwork, larger than the text around them, for fields that
 * can only hold plain text (a `<textarea>` cannot draw `EmojiImage`). Put it first in the field's
 * stack: its `unicode-range` covers emoji code points only, so every other character falls through
 * to the next family, and so does an emoji whose chunk is still downloading (`font-display: swap`)
 * or that the font lacks, which draws in the platform's own emoji until then.
 *
 * 125%, a touch under the 137.5% `EmojiText` draws in a message, because a textarea's line is a
 * fixed box (20px for the 14px composer) that crops whatever overflows it. 137.5% already clips the
 * tops of the taller glyphs on some screens; a message line can grow around an image, the field's
 * cannot.
 */
export const SIZED_EMOJI_FONT = 'Natter Sized Emoji';

type CodePointRange = [number, number];

/**
 * Pictographs, the emoji in Miscellaneous Symbols and Dingbats, and the joiners, keycap, selector
 * and tag characters that hold multi-part emoji together. Noto's chunks also map digits, `#`, `©`,
 * arrows and other text-first symbols, which must keep the text font, so each chunk is cut down to
 * these ranges.
 */
const EMOJI_RANGES: CodePointRange[] = [
  [0x1f000, 0x1faff],
  [0x2600, 0x27bf],
  [0x231a, 0x231b],
  [0x23e9, 0x23fa],
  [0x2b50, 0x2b50],
  [0x2b55, 0x2b55],
  [0x200d, 0x200d],
  [0x20e3, 0x20e3],
  [0xfe0f, 0xfe0f],
  [0xe0020, 0xe007f],
];

const parseUnicodeRange = (unicodeRange: string): CodePointRange[] =>
  unicodeRange.split(',').map((part) => {
    const [start, end = start] = part.trim().replace(/^U\+/i, '').split('-');
    return [parseInt(start, 16), parseInt(end, 16)];
  });

const formatCodePoint = (codePoint: number) => codePoint.toString(16);

const formatUnicodeRange = (ranges: CodePointRange[]) =>
  ranges
    .map(([start, end]) => `U+${formatCodePoint(start)}${end > start ? `-${formatCodePoint(end)}` : ''}`)
    .join(', ');

/** The parts of a CSS `unicode-range` that fall inside `EMOJI_RANGES`. */
export const emojiOnlyUnicodeRange = (unicodeRange: string) =>
  formatUnicodeRange(
    parseUnicodeRange(unicodeRange).flatMap(([start, end]) =>
      EMOJI_RANGES.filter(([from, to]) => from <= end && to >= start).map(
        ([from, to]): CodePointRange => [Math.max(start, from), Math.min(end, to)]
      )
    )
  );

/**
 * One `@font-face` per Noto chunk, for MUI `GlobalStyles`. `tech(color-COLRv1)` hands Chrome and
 * Firefox the COLRv1 build; Safari, which cannot draw COLRv1, skips it and takes the SVG build.
 */
export const sizedEmojiFontFace = NOTO_EMOJI_FONT_CHUNKS.map((unicodeRange, chunk) => ({
  '@font-face': {
    fontFamily: SIZED_EMOJI_FONT,
    src: [
      `url(${notoEmojiFontUrl('colrv1', chunk)}) format('woff2') tech(color-COLRv1)`,
      `url(${notoEmojiFontUrl('svg', chunk)}) format('woff2')`,
    ].join(', '),
    unicodeRange: emojiOnlyUnicodeRange(unicodeRange),
    fontDisplay: 'swap',
    sizeAdjust: '125%',
  },
}));
